import { calibrateRatios, cloneRatios, countModifiedRatios, stationOf } from './domain';
import type { ContractPeriod, DegreeDays, DjuSettings, PlanProject, ProjectKind, RatioTable as Ratios, UpdateProjectInput } from './types';

/** Brouillon de la configuration : mêmes paramètres que l'assistant de création. */
export interface SettingsDraft {
  name: string;
  description: string;
  kind: ProjectKind;
  networkIds: string[];
  contract: ContractPeriod;
  /** Degrés-jours retenus pour la station (étape 2 : ratios du projet). */
  stationDegrees: DegreeDays;
  /** Mes ratios et leurs degrés-jours (étape 2 : bloc 1). */
  myDegrees: DegreeDays;
  myRatios: Ratios;
  /** Mes ratios tels qu'appliqués au projet. */
  applied: { degrees: DegreeDays; ratios: Ratios; stationDegrees: DegreeDays };
  /** Dérivés : ratios calibrés sur la station et réglage DJU, comme à la création. */
  ratios: Ratios;
  dju: DjuSettings;
}

/** Recalcule ratios et DJU du projet à partir des degrés-jours et des ratios appliqués. */
export function derive(d: Omit<SettingsDraft, 'ratios' | 'dju'> & { dju: DjuSettings }): SettingsDraft {
  return {
    ...d,
    ratios: d.applied.degrees.dju === d.applied.stationDegrees.dju && d.applied.degrees.djclim === d.applied.stationDegrees.djclim
      ? cloneRatios(d.applied.ratios) : calibrateRatios(d.applied.ratios, d.applied.degrees, d.applied.stationDegrees),
    dju: { stationId: d.dju.stationId, reference: d.applied.stationDegrees.dju, djclim: d.applied.stationDegrees.djclim },
  };
}

export function toDraft(p: PlanProject): SettingsDraft {
  const degrees = { dju: p.dju.reference, djclim: p.dju.djclim ?? stationOf(p.dju).djclim };
  return {
    name: p.name, description: p.description ?? '', kind: p.kind, networkIds: [...p.networkIds], contract: { ...p.contract },
    stationDegrees: degrees, myDegrees: { ...degrees }, myRatios: cloneRatios(p.ratios),
    applied: { degrees: { ...degrees }, ratios: cloneRatios(p.ratios), stationDegrees: { ...degrees } },
    ratios: cloneRatios(p.ratios), dju: { ...p.dju },
  };
}

export const same = (a: SettingsDraft, b: SettingsDraft) =>
  JSON.stringify({ ...a, name: a.name.trim(), description: a.description.trim() })
  === JSON.stringify({ ...b, name: b.name.trim(), description: b.description.trim() });

/** Mes ratios ont changé depuis leur dernière application au projet. */
export const isPending = (d: SettingsDraft) =>
  countModifiedRatios(d.applied.ratios, d.myRatios) > 0
  || d.applied.degrees.dju !== d.myDegrees.dju
  || d.applied.degrees.djclim !== d.myDegrees.djclim
  || d.applied.stationDegrees.dju !== d.stationDegrees.dju
  || d.applied.stationDegrees.djclim !== d.stationDegrees.djclim;

export const toUpdate = (d: SettingsDraft): UpdateProjectInput => ({
  name: d.name.trim(), description: d.description.trim(), kind: d.kind, networkIds: d.networkIds, contract: d.contract, ratios: d.ratios, dju: d.dju,
});
